import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Button,
  Flex,
  FormControl,
  Link,
  Text,
  Textarea,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react'
import type {
  GeneralParamsDto,
  InsuranceParamsDto,
  InsuranceParamsPropertyDto,
  InsuranceParamsTravelDto
} from 'BrokerTsLib/src/dtos'
import {
  formatPersonFullName
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useContext, useEffect, useState } from 'react'

import { Card, CardBody } from '../../components/Card'
import { FormLabel } from '../../components/FormLabel'
import { Loading } from '../../components/Loading'
import { stepsContext } from '../../context/StepsContext'
import { carkulkaOffersContext } from '../../context/CarkulkaOffersProvider'
import { useApp } from '../../hooks/useApp'
import { useUrl as useDocumentUrl } from '../../hooks/useDocuments'
import { Email, Print, Save } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { formatDate } from '../../utils/formatDate'
import { useSendCalculationContext } from '../SendCalculationModal'
import { EmailAddress } from '../shared/EmailAddress'
import { MobilePhone } from '../shared/MobilePhone'
import { InsuranceInfoBasic } from './InsuranceInfoBasic'
import { InsuranceInfoBroker } from './InsuranceInfoBroker'
import { InsuranceInfoDetailsProperty } from './InsuranceInfoDetailsProperty'
import { InsuranceInfoDetailsTravel } from './InsuranceInfoDetailsTravel'
import { InsuranceInfoDetailsVehicle } from './InsuranceInfoDetailsVehicle'
import { InsuranceInfoFooter } from './InsuranceInfoFooter'
import { InsuranceInfoItem } from './InsuranceInfoItem'
import { InsuranceInfoNote } from './InsuranceInfoNote'
import { InsuranceInfoWrapper } from './InsuranceInfoWrapper'

export type InsuranceInfoFooterProps = BoxProps

export interface InsuranceInfoWrapperProps extends BoxProps {
  isSticky: boolean
}

const getOffset = (element: Element | null) => element?.getBoundingClientRect()?.y

export const InsuranceInfo = () => {
  const {
    insuranceParams: insuranceParamsGeneric,
    isLoadingData,
    insuranceParamsId: paramsId,
    isLoading,
  } = useInsuranceParamsContext()
  const { app, product } = useApp()

  const insuranceParamsVehicle =
    product == Product.VehicleInsurance || product == Product.VehicleLeasingInsurance
      ? (insuranceParamsGeneric as InsuranceParamsDto)
      : undefined

  const insuranceParamsTravel =
    product == Product.TravelInsurance
      ? (insuranceParamsGeneric as InsuranceParamsTravelDto)
      : undefined

  const insuranceParamsProperty =
    [Product.PropertyInsurance, Product.LiabilityInsurance].includes(product!)
      ? (insuranceParamsGeneric as InsuranceParamsPropertyDto)
      : undefined

  const { step: currentStep } = useContext(stepsContext)

  const insuranceInfoWrapper = document.querySelector('.js-insurance-info-wrapper')
  const [isSticky, setIsSticky] = useState(false)
  const [showNoteTextarea, setShowNoteTextarea] = useBoolean(false)
  const [note, setNote] = useState('')
  const shouldStick = isSticky && !showNoteTextarea

  useEffect(() => {
    const listenToScroll = () => {
      const heightToHide = getOffset(insuranceInfoWrapper) || 0

      setIsSticky(heightToHide <= 0)
    }

    const scrollInterval = setInterval(listenToScroll, 16)

    return () => clearInterval(scrollInterval)
  }, [insuranceInfoWrapper])

  const handleSaveNote = (event: any) => {
    const value = event.target.value.trim()

    setNote(value)
    setShowNoteTextarea.off()
  }

  let generalParams: GeneralParamsDto | undefined = undefined
  let insuranceParamsAvailable = false
  if (insuranceParamsVehicle) {
    generalParams = insuranceParamsVehicle.generalParams
    insuranceParamsAvailable = true
  } else if (insuranceParamsTravel) {
    generalParams = insuranceParamsTravel.generalParams
    insuranceParamsAvailable = true
  } else if (insuranceParamsProperty) {
    generalParams = insuranceParamsProperty.generalParams
    insuranceParamsAvailable = true
  }

  const createdBy = generalParams?.createdBy
  const phEmail = insuranceParamsGeneric?.policyHolder.person.email
  let phPhone = insuranceParamsGeneric?.policyHolder.person.mobilePhone
  // předvolbu zobrazujeme pouze u čísel, která nejsou tuzemská
  if (
    phPhone &&
    insuranceParamsGeneric?.policyHolder.person.mobilePhonePrefix &&
    insuranceParamsGeneric?.policyHolder.person.mobilePhonePrefix != '+420'
  ) {
    phPhone = insuranceParamsGeneric?.policyHolder.person.mobilePhonePrefix + phPhone
  }
  if (!phPhone) {
    phPhone = insuranceParamsGeneric?.policyHolder.person.phone
  }

  return (
    <InsuranceInfoWrapper className="js-insurance-info-wrapper" isSticky={shouldStick}>
      <Card borderTopRadius={shouldStick ? 'none' : 'md'}>
        <CardBody sx={{ display: 'grid', p: 4, gap: 2 }}>
          {(!insuranceParamsAvailable || isLoading) && <Loading large={true} />}

          {insuranceParamsAvailable && (
            <>
              <InsuranceInfoBasic className="insurance-info">
                {paramsId && <InsuranceInfoItem label="Nabídka pojištění" value={paramsId} />}

                {generalParams?.insuranceStartDate && (
                  <InsuranceInfoItem
                    label="Počátek pojištění"
                    value={formatDate({ date: new Date(generalParams.insuranceStartDate) })}
                  />
                )}

                {insuranceParamsVehicle &&
                  <InsuranceInfoDetailsVehicle {...insuranceParamsVehicle} />
                }

                {insuranceParamsTravel &&
                  <InsuranceInfoDetailsTravel {...insuranceParamsTravel} />
                }

                {insuranceParamsProperty && product == Product.PropertyInsurance &&
                  <InsuranceInfoDetailsProperty {...insuranceParamsProperty} />
                }
                {insuranceParamsProperty && product == Product.LiabilityInsurance &&
                  <InsuranceInfoItem
                    label="Pojistník"
                    value={formatPersonFullName(insuranceParamsProperty?.policyHolder?.person)}
                  />
                }


                {phEmail && (
                  <InsuranceInfoItem
                    label="E-mail pojistníka"
                    value={<EmailAddress email={phEmail} showIcon={false} />}
                  />
                )}

                {phPhone && (
                  <InsuranceInfoItem
                    label="Telefon pojistníka"
                    value={<MobilePhone mobilePhone={phPhone} showIcon={false} />}
                  />
                )}

              </InsuranceInfoBasic>

              <InsuranceInfoFooter as="footer" sx={{ alignItems: 'end' }}>
                {createdBy && (
                  <InsuranceInfoBroker
                    broker={createdBy}
                    date={new Date(generalParams!.created!)}
                  />
                )}

                <Flex sx={{ gap: 2 }} className="insurance-info__footer-buttons">
                  {currentStep == 3 && (app === 'carkulka' || app == 'cestovko' || app == 'chaloupka') && (
                    <SendCalculationButton />
                  )}

                  {/* {false && (
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={setShowNoteTextarea.toggle}
                    >
                      <Note color="secondary.500" />
                      Napsat poznámku
                    </Button>
                  )} */}

                  {showNoteTextarea && (
                    <Button
                      variant="solid"
                      colorScheme="primary"
                      size="sm"
                      type="button"
                      onClick={setShowNoteTextarea.off}
                    >
                      <Save color="white" />
                      Uložit poznámku
                    </Button>
                  )}
                </Flex>
              </InsuranceInfoFooter>
            </>
          )}

          {showNoteTextarea && (
            <FormControl variant="fullWidth">
              <FormLabel>Poznámka ke kalkulaci</FormLabel>
              <Textarea autoFocus defaultValue={note} onBlur={handleSaveNote} />
            </FormControl>
          )}

          {note && !showNoteTextarea && (
            <InsuranceInfoNote
              sx={{ transition: 'all 100ms', opacity: isSticky ? { lg: 0 } : 1 }}
              onClick={setShowNoteTextarea.on}
            >
              <Text variant="textMuted">{note}</Text>
            </InsuranceInfoNote>
          )}
        </CardBody>
      </Card>
    </InsuranceInfoWrapper>
  )
}

const SendCalculationButton = () => {
  const { insuranceParamsId, insuranceParams } = useInsuranceParamsContext()
  const { onOpen: openSendCalculation, isDisabled, order } = useSendCalculationContext()
  const { calculatingOffersIds } = useContext(carkulkaOffersContext)
  const isCalculatingInProgress = (calculatingOffersIds ?? []).length > 0
  const documentUrl = useDocumentUrl()
  const buttonDisabled = isDisabled || isCalculatingInProgress
  const disabledText = isCalculatingInProgress
    ? 'Probíhá výpočet nabídek.'
    : isDisabled ? 'Alespoň jedna kalkulace musí být validní a označena jako aktivní.' : ''
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  return <>
    {!isContracted &&
      <Tooltip label={disabledText} hasArrow>
        <Button
          variant="outline"
          size="sm"
          isLoading={isCalculatingInProgress}
          loadingText='E-mail'
          type="button"
          onClick={openSendCalculation}
          isDisabled={buttonDisabled}
        >
          <Email color="secondary.500" />
          E-mail
        </Button>
      </Tooltip>
    }

    {!isContracted &&
      <Tooltip label={disabledText} hasArrow>
        <Button
          as={Link}
          href={buttonDisabled ? undefined : `${documentUrl}/GetOffersPdf?paramsId=${insuranceParamsId}&order=${order}&report=0`}
          target={buttonDisabled ? undefined : '_blank'}
          variant="outline"
          size="sm"
          type="button"
          isLoading={isCalculatingInProgress}
          loadingText='Tisk'
          isDisabled={buttonDisabled}
          _hover={{ textDecoration: 'none', bg: 'gray.100' }}
        >
          <Print color="secondary.500" />
          Tisk
        </Button>
      </Tooltip>
    }
  </>
}